import { useQueryClient } from "@tanstack/react-query";
import { addDoc, collection, doc, getFirestore, setDoc } from "firebase/firestore";
import { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { crearUsuarioSinAfectarSesionActual } from "../../firebase/firebaseConfig";
import { queryKeys } from "../../queries/queryKeys";
import type {
  RegisterFormEvent,
  RegisterFormState,
  RegisterFormValues,
  RegisterInputChangeEvent,
  UseRegisterPageParams,
} from "./types";

export const crearEmailRegistro = (usuario: string): string => {
  return `${usuario.trim().toLowerCase()}@fastadmin.com`;
};

export const normalizarTexto = (valor: string): string => {
  return valor.trim();
};

export const normalizarCantidadRutas = (rutas: number): number => {
  if (!Number.isFinite(rutas) || rutas < 1) {
    return 1;
  }

  return Math.floor(rutas);
};

export const crearProximoPago = (fechaBase = new Date()): Date => {
  const proximoPago = new Date(fechaBase);
  proximoPago.setMonth(proximoPago.getMonth() + 1);
  return proximoPago;
};

export const contrasenasCoinciden = (contrasena: string, contrasenaVerify: string): boolean => {
  return contrasena === contrasenaVerify;
};

export const formularioRegistroEsValido = ({
  usuario,
  nombre,
  contrasena,
  contrasenaVerify,
  rutas,
}: RegisterFormState): boolean => {
  return (
    usuario.trim().length > 0 &&
    nombre.trim().length > 0 &&
    contrasena.trim().length > 0 &&
    contrasenaVerify.trim().length > 0 &&
    rutas > 0
  );
};

export const useRegisterPage = ({ navigate }: UseRegisterPageParams) => {
  const [usuario, setUsuario] = useState("");
  const [nombre, setNombre] = useState("");
  const [contrasena, setContrasena] = useState("");
  const [contrasenaVerify, setContrasenaVerify] = useState("");
  const [isSubmiting, setIsSubmiting] = useState(false);
  const [isButtonPassword, setIsButtonPassword] = useState(true);
  const [rutas, setRutas] = useState(1);
  const queryClient = useQueryClient();

  const formValues = useMemo<RegisterFormValues>(
    () => ({
      usuarioNormalizado: normalizarTexto(usuario).toLowerCase(),
      nombreNormalizado: normalizarTexto(nombre),
      rutasNormalizadas: normalizarCantidadRutas(rutas),
    }),
    [nombre, rutas, usuario],
  );

  const passwordsMatch = useMemo(
    () => contrasenasCoinciden(contrasena, contrasenaVerify),
    [contrasena, contrasenaVerify],
  );

  const changeTypeText = useCallback(() => {
    setIsButtonPassword((prev) => !prev);
  }, []);

  const handleUsuarioChange = useCallback((event: RegisterInputChangeEvent) => {
    setUsuario(event.target.value);
  }, []);

  const handleNombreChange = useCallback((event: RegisterInputChangeEvent) => {
    setNombre(event.target.value);
  }, []);

  const handleRutasChange = useCallback((event: RegisterInputChangeEvent) => {
    setRutas(normalizarCantidadRutas(Number(event.target.value)));
  }, []);

  const handleContrasenaChange = useCallback((event: RegisterInputChangeEvent) => {
    setContrasena(event.target.value);
  }, []);

  const handleContrasenaVerifyChange = useCallback((event: RegisterInputChangeEvent) => {
    setContrasenaVerify(event.target.value);
  }, []);

  const canSubmit = useMemo(
    () =>
      formularioRegistroEsValido({
        usuario,
        nombre,
        contrasena,
        contrasenaVerify,
        rutas,
      }) &&
      passwordsMatch &&
      isSubmiting === false,
    [contrasena, contrasenaVerify, isSubmiting, nombre, passwordsMatch, rutas, usuario],
  );

  const handleRegister = useCallback(async () => {
    if (isSubmiting) {
      return;
    }

    if (
      !formularioRegistroEsValido({
        usuario,
        nombre,
        contrasena,
        contrasenaVerify,
        rutas,
      })
    ) {
      toast.error("Debes completar todos los campos correctamente.");
      return;
    }

    if (!passwordsMatch) {
      toast.error("Las contrasenas no coinciden");
      return;
    }

    setIsSubmiting(true);

    try {
      const userEmail = crearEmailRegistro(formValues.usuarioNormalizado);
      const ultimoPago = new Date();
      const proximoPago = crearProximoPago(ultimoPago);

      // Crear nuevo usuario en Firebase Authentication sin afectar la sesion
      // del admin actual (ver crearUsuarioSinAfectarSesionActual).
      const createdUser = await crearUsuarioSinAfectarSesionActual(userEmail, contrasena);

      const db = getFirestore();
      const adminUsersRef = collection(db, "admin_users");
      const userDoc = doc(adminUsersRef, createdUser.uid);
      const adminPassRef = collection(db, "adminPass");

      await Promise.all([
        setDoc(userDoc, {
          uid: createdUser.uid,
          email: createdUser.email,
          isAdmin: true,
          nombre: formValues.nombreNormalizado,
          cantidadRutas: formValues.rutasNormalizadas,
          ultimoPago,
          proximoPago,
          activo: true,
        }),
        addDoc(adminPassRef, {
          email: createdUser.email,
          contrasena,
          nombre: formValues.nombreNormalizado,
          uid: createdUser.uid,
        }),
      ]);

      // La lista de admins (visible para el super-admin en HomePage) no se
      // parchea localmente aqui; invalidamos para que refleje el admin nuevo
      // de inmediato en vez de esperar a que expire el staleTime.
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminUsersList() });

      navigate("/home");
      toast.success("Admin creado con exito");
    } catch (error: any) {
      console.log(error);

      if (error?.code === "auth/email-already-in-use") {
        toast.error("El email ya esta en uso, pruebe usando otro");
      } else if (error?.code === "auth/weak-password") {
        toast.error("La contrasena debe tener un minimo de seis (6) caracteres");
      } else {
        toast.error("No se pudo crear el administrador.");
      }
    } finally {
      setIsSubmiting(false);
    }
  }, [
    contrasena,
    formValues,
    isSubmiting,
    navigate,
    nombre,
    passwordsMatch,
    queryClient,
    contrasenaVerify,
    rutas,
    usuario,
  ]);

  const handleSubmit = useCallback(
    async (event: RegisterFormEvent) => {
      event.preventDefault();
      await handleRegister();
    },
    [handleRegister],
  );

  return {
    isSubmiting,
    isButtonPassword,
    rutas,
    canSubmit,
    changeTypeText,
    handleUsuarioChange,
    handleNombreChange,
    handleRutasChange,
    handleContrasenaChange,
    handleContrasenaVerifyChange,
    handleSubmit,
  };
};
